/* eslint-disable @typescript-eslint/naming-convention */
// import { ImagePlaceHolder } from "@/components/images"
import { Box, Button, Flex, Text } from "@chakra-ui/react"
import { useMediaQuery } from "@uidotdev/usehooks"
import React, { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import bg from "../../assets/images/all-products.png"
import { Paths, breadCrumbAllProductsSecondLevel, breadCrumbFirstLevel, mobileBreakpoint } from "../../common/commonConstants"
import { useIsLoggedIn } from "../../components/commons/utils"
import { BreadcrumbConfigs, BreadcrumbSEO, SEOConfigs, SEOHead } from "../../components/SEO"
import { SubPage, SubPageContent } from "../../components/SubPage"
import { BreadCrumb } from "../../components/commons/breadcrumb"
import { Heading2CapsSubText } from "../../components/commons/heading2CapsSubText"
import { ProductThumbnailType2 } from "../../components/commons/productThumbnailType2"
import { StaticPageHeader } from "../../components/commons/static-page-header"
import { useAllCollarsQuery, type Product4Menu } from "../../type-and-hooks"
import { CreateNewFMP } from "../fmp/createNewFMPButton.main"

// Products arrive from the API already sorted by sub_category_display_order (see SQL ORDER BY).
// Preserve insertion order when grouping, then force Sealant before Accessory at the end.
const groupBySubcategory = (products: (Product4Menu | null | undefined)[]): [string, Product4Menu[]][] => {
  const groups = new Map<string, Product4Menu[]>()
  for (const item of products) {
    if (!item) continue
    const key = item.subcategory ?? "unknown"
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(item)
  }
  const entries = [...groups.entries()]
  const isTrailing = (key: string): boolean =>
    key.toLowerCase().includes("sealant") || key.toLowerCase().includes("accessor")
  const regular = entries.filter(([key]) => !isTrailing(key))
  const sealant = entries.filter(([key]) => key.toLowerCase().includes("sealant"))
  const accessory = entries.filter(([key]) => key.toLowerCase().includes("accessor"))
  return [...regular, ...sealant, ...accessory]
}

export const AllProducts = (): JSX.Element => {
  const navigate = useNavigate()
  const { loading, data } = useAllCollarsQuery({ variables: { collarTypeId: "0" }, fetchPolicy: "cache-and-network" })

  const handleClick = (item: Product4Menu | undefined | null): void => {
    if (!item?.pid) {
      console.error("pid is not available", item)
    }
    if (item) navigate("" + item?.prod_link)
  }

  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`)
  const isLoggedIn = useIsLoggedIn()
  const columnCount = isMobile ? 2 : 3

  const groupedProducts = useMemo(() => groupBySubcategory(data?.allCollars ?? []), [data?.allCollars])

  return (
    <>
      <SEOHead {...SEOConfigs.products} canonicalUrl="https://snapfiresystems.com/products" />
      <BreadcrumbSEO items={BreadcrumbConfigs.products} />
      <SubPage>
        <StaticPageHeader title="All Products" bg={bg} />
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbAllProductsSecondLevel,
            thirdLevel: { title: "See All" }
          }}
        />
        <Box mt={["24px", "24px", "24px", "30px"]} textAlign={"center"} px={[2, 0, 0]}>
          <Text variant={["RBody", "RBody", "RBody", "Body1Regular"]}>Click any product for more details and specs or use our</Text>
          <Flex justify="center" mt={["12px", "12px", "12px", "16px"]}>
            <Button
              variant="Button1RegularOutline"
              px="18px"
              fontSize={["12px", "16px", "16px", "16px"]}
              fontWeight="400"
              borderColor="primary"
              borderWidth="1px"
              mr={["20px", "20px", "20px", "24px"]}
              onClick={() => {
                navigate(isLoggedIn ? Paths.FMP.PRODUCT_FILTER : Paths.PRODUCT_FILTER)
              }}
            >
              Product Filter
            </Button>
            <CreateNewFMP buttonText="Create New FMP" buttonProps={{ variant: "Button1RegularAccentWithShadow", px: "18px" }} />
          </Flex>
        </Box>
        <SubPageContent>
          {loading && (
            <Text textAlign={"center"} p="4">
              Loading...
            </Text>
          )}

          <Box overflow={"hidden"} mb={["30px", "30px", "30px", "137px", "137px", "137px"]}>
            {groupedProducts.map(([subcategory, collars], index) => (
              <React.Fragment key={subcategory}>
                <Heading2CapsSubText
                  headingTextStyles={{
                    textAlign: "left",
                    fontSize: ["24px", "24px", "24px", "24px"],
                    fontWeight: 500,
                    lineHeight: "24px",
                    fontFamily: "Work Sans",
                    pt: index === 0 ? "10px" : "40px",
                    pb: "14px"
                  }}
                  subTextStyles={{ color: "#939598" }}
                  headingText={subcategory !== "unknown" ? subcategory : ""}
                  subText={`${collars.length} items`}
                />
                <Box display="grid" gridTemplateColumns={[`repeat(${columnCount}, 1fr)`]} gap="2px" justifyItems="stretch" alignItems="stretch">
                  {collars.map((item, idx) => (
                    <React.Fragment key={`${subcategory}_${idx}_allCollars`}>
                      <ProductThumbnailType2
                        onClickCallBack={() => {
                          handleClick(item)
                        }}
                        prodCode={item?.prod_code}
                        prodPhoto={item?.prod_photo}
                      />
                      <>{(idx + 1) % columnCount === 0 && <Box my="16px" gridColumn={`span ${columnCount}`} height="1px" bg="primary"></Box>}</>
                    </React.Fragment>
                  ))}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </SubPageContent>
      </SubPage>
    </>
  )
}
